// MED_ORDERS — github.com/Simple-Life-Medical/med-orders
// OpenAPI: github.com/Simple-Life-Medical/med-orders/blob/develop/.simple/openapi.yml
//
// The Orders section (KOVA-797): the order list, one order's details and the banner feed the renewal
// card is picked from. Like med-billing-engine the service derives the user from the DPoP-bound Auth0
// token, so no id is sent. Every path sits under the service prefix `/orders` (the spec's
// `servers: /orders`). Only `authedJson`'s own headers go out: med-orders' CORS allows `Content-Type`,
// `Authorization` and `DPoP` and nothing else, so an extra header (the mobile app's
// `x-kova-app-timezone`) would fail the preflight. See docs/architecture/api.md.
import { authedJson } from '@/api/http'
import { config } from '@/config'
import { buildFetchApiErrorContext, captureApiError } from '@/sentry/network'

const REPO = 'med-orders' // Sentry `api` tag
const BASE = `${config.medOrdersHost}/orders`
const NONCE_ID = 'med-orders' // per-backend DPoP-nonce cache key

// ── Wire (DTO) side ──────────────────────────────────────────────────────────────────────────────
// Only the fields the contract has. Every nullable property arrives as an explicit `null` (no
// `omitempty` on the backend); absent and `null` are read the same. Enum-valued fields are `string`
// here because the contract's enums grow without notice — the mappers below degrade an unknown value
// instead of trusting the declared union (ADR 0010 decision 3, like `mapRxStatus` in medBilling).

export interface MedicationDTO {
	/** "Semaglutide" / "Tirzepatide", or the backend's fallback "GLP-1 medication". */
	name: string
	/** Always `null` on the list; often `null` on the details. Not rendered (KOVA-625/634). */
	doseAmount?: number | null
	doseUnit?: string | null
	quantity?: number | null
	unit?: string | null
}

export interface OrderItemDTO {
	/** Billing order UUID — the details lookup key. */
	id: string
	/** Telegra order number: display only, never a lookup key. */
	orderNumber: string
	/** submitted | approved | shipped | delivered | canceled | delivery_failed | returned */
	status: string
	/** submitted | reviewing (with `submitted`); approved | dispensed (with `approved`); else `null`. */
	substatus?: string | null
	/** RFC 3339; non-null exactly when the status is terminal (delivered included). */
	completedAt?: string | null
	medication: MedicationDTO
}

export interface NextShipmentDTO {
	/** `YYYY-MM-DD` — the day the medication should leave the pharmacy (US Eastern), not a delivery date. */
	estimatedShipAt: string
	/** refill | new_rx | mid_cycle — not rendered. */
	type: string
}

export interface OrdersResponseDTO {
	/** The server decides the section; newest-created first, kept as sent. Never `null`, may be `[]`. */
	inProgress: OrderItemDTO[]
	past: OrderItemDTO[]
	/** Not rendered — removed from the mobile app too (KOVA-633). */
	supply?: { dosesLeft?: number | null; nextFulfilmentDate?: string | null } | null
	/** `null` when nothing is scheduled or the estimate is unavailable. */
	nextShipment?: NextShipmentDTO | null
}

export interface TimelineEntryDTO {
	status: string
	substatus?: string | null
	/** RFC 3339. Past events only, oldest first. */
	at: string
}

/** The user's CURRENT address from med-vault, not a per-order snapshot (Telegra keeps none). */
export interface ShippingDetailsDTO {
	addressLine1: string
	addressLine2?: string | null
	city: string
	state: string
	/** ISO 3166-1 alpha-2. Not rendered, so not mapped. */
	country: string
	postalCode: string
	recipient: string
	phone: string
}

/** The customer's default method at read time. `brand` / `last4` for card-based methods only. */
export interface PaymentDetailsDTO {
	/** card | paypal | apple_pay | google_pay | klarna */
	type: string
	/** Free-form, as the provider reports it ("mastercard", "VISA"). */
	brand?: string | null
	last4?: string | null
}

export interface DeliveryTrackingDTO {
	/** ups | usps | fedex | dhl | unknown */
	carrier: string
	/** EasyPost public page; may be `null` with a known carrier. There is no tracking number. */
	trackingUrl?: string | null
}

export interface OrderResponseDTO extends OrderItemDTO {
	/** `YYYY-MM-DD`, only while shipped with an ETA. */
	arrivalFrom?: string | null
	/** `arrivalFrom` + 3 days, synthesised by the backend. */
	arrivalTo?: string | null
	/** Required by the contract, may be `[]`. */
	timeline: TimelineEntryDTO[]
	shipping?: ShippingDetailsDTO | null
	payment?: PaymentDetailsDTO | null
	/** Only for shipped / delivered / delivery_failed / returned. */
	tracking?: DeliveryTrackingDTO | null
}

export interface BannerDTO {
	id: string
	/** order_status | order_compact | action_needed | no_prescription | intake_incomplete */
	type: string
	/** Set on health-check actions; `null` on payment actions (they are subscription-scoped). */
	orderId?: string | null
	status?: string | null
	substatus?: string | null
	/** health_check_due | health_check_expired | payment_needed */
	action?: string | null
	/** neutral | warning | critical — computed by the server from the action's age. */
	escalation?: string | null
	/** Always `null` today. */
	actionDueDate?: string | null
	/**
	 * Built by billing and copied verbatim: any scheme passes the backend, and the links are stamped for
	 * the mobile app (`from=app`). The intake link's `session_id` is a bearer capability for intake PHI —
	 * never log it or put it in Sentry. See docs/architecture/api.md → med-orders.
	 */
	url?: string | null
	medication?: MedicationDTO | null
	arrivalFrom?: string | null
	arrivalTo?: string | null
	tracking?: DeliveryTrackingDTO | null
	completedAt?: string | null
}

export interface BannerListResponseDTO {
	/** Required; never `null`, may be `[]`. List order is display order. */
	items: BannerDTO[]
}

/** The error body. Branch on the HTTP status (or `code`) — `message` is never rendered. */
export interface OrdersErrorDTO {
	/** malformed_request | not_found | unauthorized | forbidden | client_error | internal_error | service_unavailable */
	code?: string
	message?: string
}

// ── Domain side (what the store and views read) ──────────────────────────────────────────────────

export type OrderStatus =
	| 'submitted'
	| 'approved'
	| 'shipped'
	| 'delivered'
	| 'canceled'
	| 'delivery_failed'
	| 'returned'
export type OrderSubstatus = 'submitted' | 'reviewing' | 'approved' | 'dispensed'
export type Carrier = 'ups' | 'usps' | 'fedex' | 'dhl' | 'unknown'
export type PaymentMethodType = 'card' | 'paypal' | 'apple_pay' | 'google_pay' | 'klarna'
export type BannerType = 'order_status' | 'order_compact' | 'action_needed' | 'no_prescription' | 'intake_incomplete'
export type BannerAction = 'health_check_due' | 'health_check_expired' | 'payment_needed'
export type BannerEscalation = 'neutral' | 'warning' | 'critical'

export interface OrderSummary {
	id: string
	orderNumber: string
	/** `null` = a value this app doesn't know: the order is kept (and opens), its badge is omitted, never guessed. */
	status: OrderStatus | null
	substatus: OrderSubstatus | null
	completedAt: string | null
	/** The list cards and the details show the name only (KOVA-625/634). */
	medicationName: string
}

export interface OrdersOverview {
	inProgress: OrderSummary[]
	past: OrderSummary[]
	/** `nextShipment.estimatedShipAt` verbatim (`YYYY-MM-DD`); the formatter validates it. */
	nextShipmentDate: string | null
}

export interface OrderTimelineEntry {
	status: OrderStatus | null
	/**
	 * `null` = none sent; `'unknown'` = one this app doesn't know. Kept apart, unlike the order's own substatus: the
	 * timeline lands a `submitted` / `approved` event with no substatus on that status's own row, and one with an
	 * unknown substatus on no row (the mobile app compares the raw value) — read as `null`, a future
	 * `approved` + `packing` would move "Prescription approved" to its time.
	 */
	substatus: OrderSubstatus | 'unknown' | null
	at: string
}

export type OrderShipping = Omit<ShippingDetailsDTO, 'country'> & { addressLine2: string | null }

export interface OrderPayment {
	/** `null` = a method this app doesn't know. */
	type: PaymentMethodType | null
	brand: string | null
	last4: string | null
}

export interface OrderTracking {
	/** An unknown carrier reads as the contract's own `unknown` ("Track order"), never drops the block. */
	carrier: Carrier
	trackingUrl: string | null
}

export interface OrderDetail extends OrderSummary {
	arrivalFrom: string | null
	arrivalTo: string | null
	timeline: OrderTimelineEntry[]
	shipping: OrderShipping | null
	payment: OrderPayment | null
	tracking: OrderTracking | null
}

/** One banner of the feed, reduced to what the Orders section reads (the renewal card, spec §2.6). */
export interface OrdersBanner {
	id: string
	type: BannerType
	orderId: string | null
	/** `null` = absent or unknown; a known action is kept even where the web renders nothing for it. */
	action: BannerAction | null
	/** `null` = absent or unknown — the renewal card reads it as `neutral`. */
	escalation: BannerEscalation | null
	/** Verbatim — see `BannerDTO.url` before following it. */
	url: string | null
}

// ── Mappers ──────────────────────────────────────────────────────────────────────────────────────

const ORDER_STATUSES: Record<OrderStatus, true> = {
	submitted: true,
	approved: true,
	shipped: true,
	delivered: true,
	canceled: true,
	delivery_failed: true,
	returned: true,
}

const ORDER_SUBSTATUSES: Record<OrderSubstatus, true> = {
	submitted: true,
	reviewing: true,
	approved: true,
	dispensed: true,
}

const CARRIERS: Record<Carrier, true> = { ups: true, usps: true, fedex: true, dhl: true, unknown: true }

const PAYMENT_METHOD_TYPES: Record<PaymentMethodType, true> = {
	card: true,
	paypal: true,
	apple_pay: true,
	google_pay: true,
	klarna: true,
}

const BANNER_TYPES: Record<BannerType, true> = {
	order_status: true,
	order_compact: true,
	action_needed: true,
	no_prescription: true,
	intake_incomplete: true,
}

const BANNER_ACTIONS: Record<BannerAction, true> = {
	health_check_due: true,
	health_check_expired: true,
	payment_needed: true,
}

const BANNER_ESCALATIONS: Record<BannerEscalation, true> = { neutral: true, warning: true, critical: true }

/**
 * `value` when it is one of `allowed`'s keys, else `null`. `Object.hasOwn`, not `in`: `in` walks the
 * prototype chain, so "toString" would read as a known value.
 */
function known<T extends string>(allowed: Record<T, true>, value: string | null | undefined): T | null {
	return value != null && Object.hasOwn(allowed, value) ? (value as T) : null
}

/** Picks fields one by one (never spreads the DTO), so `supply`, the dose and `country` stay out. */
function mapOrderSummary(dto: OrderItemDTO): OrderSummary {
	return {
		id: dto.id,
		orderNumber: dto.orderNumber,
		status: known(ORDER_STATUSES, dto.status),
		substatus: known(ORDER_SUBSTATUSES, dto.substatus),
		completedAt: dto.completedAt ?? null,
		medicationName: dto.medication.name,
	}
}

/**
 * `GET /orders` → the overview. Server order and section membership are kept as sent — the client never
 * re-sorts or moves an order between sections. An order with an unknown `status` is kept with
 * `status: null` (the mobile app's types are closed; this is the web's robustness rule): an order is
 * never dropped.
 */
export function mapOrdersResponse(dto: OrdersResponseDTO): OrdersOverview {
	return {
		inProgress: dto.inProgress.map(mapOrderSummary),
		past: dto.past.map(mapOrderSummary),
		nextShipmentDate: dto.nextShipment?.estimatedShipAt ?? null,
	}
}

/**
 * `GET /orders/{id}` → one order's details. Unknown status / substatus → `null` (a timeline entry's unknown
 * substatus → `'unknown'`, see `OrderTimelineEntry`; no entry is dropped), unknown carrier → `'unknown'`, unknown
 * payment `type` → `null`; a `timeline` the backend left out reads as none.
 */
export function mapOrderResponse(dto: OrderResponseDTO): OrderDetail {
	const { shipping, payment, tracking } = dto
	return {
		...mapOrderSummary(dto),
		arrivalFrom: dto.arrivalFrom ?? null,
		arrivalTo: dto.arrivalTo ?? null,
		timeline: (dto.timeline ?? []).map((entry) => ({
			status: known(ORDER_STATUSES, entry.status),
			substatus: entry.substatus == null ? null : (known(ORDER_SUBSTATUSES, entry.substatus) ?? 'unknown'),
			at: entry.at,
		})),
		shipping: shipping
			? {
					addressLine1: shipping.addressLine1,
					addressLine2: shipping.addressLine2 ?? null,
					city: shipping.city,
					state: shipping.state,
					postalCode: shipping.postalCode,
					recipient: shipping.recipient,
					phone: shipping.phone,
				}
			: null,
		payment: payment
			? {
					type: known(PAYMENT_METHOD_TYPES, payment.type),
					brand: payment.brand ?? null,
					last4: payment.last4 ?? null,
				}
			: null,
		tracking: tracking
			? { carrier: known(CARRIERS, tracking.carrier) ?? 'unknown', trackingUrl: tracking.trackingUrl ?? null }
			: null,
	}
}

/**
 * `null` for a banner the app can't use at all: no `id`, or a `type` from a newer contract (no layout to
 * pick). Anything else unknown degrades that one field to `null` and the banner is kept.
 */
function mapBanner(dto: BannerDTO): OrdersBanner | null {
	const type = known(BANNER_TYPES, dto.type)
	if (!dto.id || type === null) return null
	return {
		id: dto.id,
		type,
		orderId: dto.orderId ?? null,
		action: known(BANNER_ACTIONS, dto.action),
		escalation: known(BANNER_ESCALATIONS, dto.escalation),
		url: dto.url ?? null,
	}
}

/**
 * `GET /banners` → the feed, in the server's order (list order is display order — never re-sorted).
 * Port of the mobile app's `apiToOrdersBanners` (`banners-from-api.ts`), narrowed to the fields the
 * Orders section reads. `items` itself is not defended here: `listBanners` refuses a 2xx without it.
 */
export function mapBannerListResponse(dto: BannerListResponseDTO): OrdersBanner[] {
	return dto.items.map(mapBanner).filter((banner): banner is OrdersBanner => banner !== null)
}

// ── Calls ────────────────────────────────────────────────────────────────────────────────────────

/** Why a 2xx answer can't be read — the `api.error_code` of its report. A fixed code, never a value from the body. */
type UnreadableReason = 'empty_response' | 'lists_missing' | 'items_missing' | 'mapping_failed'

/** The request a 2xx answer came back to: what a report about that answer is tagged with. */
interface Answer {
	url: string
	step: 'list' | 'get' | 'banners'
	status: number
}

/**
 * Report a 2xx answer this app can't read and return the error to throw. `authedJson` reports transport errors and
 * non-2xx itself, but it has returned by the time the body is checked and mapped — and it reads an empty or non-JSON
 * body as `undefined` without a report — so without this a broken answer reached the store, which turns every throw
 * into "Something went wrong", with nothing in Sentry. Same taxonomy as `authedJson`'s reports (`api.status` is the
 * 2xx, `api.error_code` the reason, level `error`), and no `body`: nothing from the payload — order numbers, the
 * address, card digits, banner links and their `session_id` — rides along (docs/architecture/sentry.md).
 */
function unreadable(answer: Answer, reason: UnreadableReason): Error {
	const error = new Error(`orders/${answer.step}: ${reason}`)
	captureApiError(
		error,
		buildFetchApiErrorContext({
			api: REPO,
			method: 'GET',
			endpoint: answer.url,
			status: answer.status,
			errorCode: reason,
			flow: 'orders',
			step: answer.step,
		}),
	)
	return error
}

/**
 * `map(data)`; a mapper that throws on a shape it doesn't know (`null` where an object is required) is reported as
 * `mapping_failed` and replaced by that error — never with its own message, whose text is the engine's, not ours.
 */
function mapAnswer<T, R>(answer: Answer, data: T, map: (dto: T) => R): R {
	try {
		return map(data)
	} catch {
		throw unreadable(answer, 'mapping_failed')
	}
}

/**
 * The user's orders, split by the server into in progress and past. There is no 404 and no paging: "no
 * orders" — and "no linked signup session", which reads the same — is a `200` with empty lists. A 2xx
 * without both lists breaks the contract and throws: "the server is broken" must not look like "you have
 * no orders". Other non-2xx and transport errors throw (`authedJson`). A 2xx it can't read is reported to
 * Sentry here (`unreadable`) before it throws.
 */
export async function listOrders(): Promise<OrdersOverview> {
	const url = `${BASE}/v1/orders`
	const { status, data } = await authedJson<OrdersResponseDTO>({
		api: REPO,
		nonceId: NONCE_ID,
		method: 'GET',
		url,
		flow: 'orders',
		step: 'list',
	})
	const answer: Answer = { url, step: 'list', status }
	if (!data) throw unreadable(answer, 'empty_response')
	if (!Array.isArray(data.inProgress) || !Array.isArray(data.past)) throw unreadable(answer, 'lists_missing')
	return mapAnswer(answer, data, mapOrdersResponse)
}

/**
 * One order's details. Resolves `null` when the order is unavailable to this user: `404` (unknown id,
 * someone else's order, a draft, an order without a Telegra id, a user with no linked signup session) or
 * `400` (an id that isn't a UUID — the store skips those before calling, so a 400 here is a backend
 * surprise). Both are `warnStatuses` (captured at `warning`, returned, not thrown). Other non-2xx and
 * transport errors throw; so does a 2xx it can't read, reported here first.
 */
export async function getOrder(orderId: string): Promise<OrderDetail | null> {
	const url = `${BASE}/v1/orders/${encodeURIComponent(orderId)}`
	const { status, data } = await authedJson<OrderResponseDTO>({
		api: REPO,
		nonceId: NONCE_ID,
		method: 'GET',
		url,
		flow: 'orders',
		step: 'get',
		warnStatuses: [400, 404],
	})
	if (status === 400 || status === 404) return null
	const answer: Answer = { url, step: 'get', status }
	if (!data) throw unreadable(answer, 'empty_response')
	return mapAnswer(answer, data, mapOrderResponse)
}

/**
 * The banner feed (`{ items: [] }` when there is nothing to show). Eligibility, precedence and
 * escalation are the server's; the client only picks what to render. A 2xx with no body or no `items`
 * array throws (as the mobile app's `listBanners` does with `empty_response`) — never read as "no
 * banners" — reported here first, like a banner the mapper can't read.
 */
export async function listBanners(): Promise<OrdersBanner[]> {
	const url = `${BASE}/v1/banners`
	const { status, data } = await authedJson<BannerListResponseDTO>({
		api: REPO,
		nonceId: NONCE_ID,
		method: 'GET',
		url,
		flow: 'orders',
		step: 'banners',
	})
	const answer: Answer = { url, step: 'banners', status }
	if (!data) throw unreadable(answer, 'empty_response')
	if (!Array.isArray(data.items)) throw unreadable(answer, 'items_missing')
	return mapAnswer(answer, data, mapBannerListResponse)
}
